import { PersonRelationType } from '@dealstack/shared/lib/generated';

import { JOIN_ALLOCATION_PIV_INFO_FORM } from './useJoinAndAllocationPIVInformationController';

/**
 * Relationship a controlling party can have to the investment vehicle — the
 * subset of {@link PersonRelationType} the investor picks.
 */
export type ControllingPartyRelationType =
	| PersonRelationType.DIRECTOR
	| PersonRelationType.UBO
	| PersonRelationType.PSC
	| PersonRelationType.TRUSTEE
	| PersonRelationType.SETTLER_OR_GRANTOR
	| PersonRelationType.PROTECTOR_OR_ENFORCER
	| PersonRelationType.BENEFICIARY
	| PersonRelationType.OTHER;

/**
 * A single controlling party entry in the Part 1 list. `localId` is a
 * client-generated id used as the field-array key and to reference the entry
 * from the Part 2 authorised-signatory selector. `relationId` is the persisted
 * PersonPerson relation id — present only for entries loaded from the backend.
 */
export interface ControllingPartyEntry {
	localId: string;
	relationId?: string;
	personId?: string;
	email: string;
	firstName: string;
	lastName: string;
	dateOfBirth: Date | null;
	placeOfBirth: string;
	countryOfBirth: string;
	citizenship: string[];
	country: string;
	city: string;
	postCode: string;
	street: string;
	houseNumber: string;
	taxResidences: ControllingPartyTaxResidenceEntry[];
	relationshipType: ControllingPartyRelationType | '';
	/** Investor-typed role; only carried when relationshipType is OTHER. */
	customRelationType: string;
}

/** One tax residency / TIN pair; a controlling party can hold several. */
export interface ControllingPartyTaxResidenceEntry {
	taxResidency: string;
	tin: string;
}

/**
 * The controlling-parties slice of {@link PIVInfoForm}, declared with a plain string
 * key. `PIVInfoForm` keys off computed enum members, so `keyof` yields enum-branded
 * types that plain string paths never match and react-hook-form's `FieldPathValue`
 * collapses to `never` — making typed `useWatch`/`setValue` on nested paths unusable.
 * The indexed access ties the shape (and the key name) back to the real form.
 */
export interface ControllingPartiesFormSlice {
	controllingParties: PIVInfoForm['controllingParties'];
}

/** Special, non-controlling-party choices for the authorised signatory. */
export const SIGNATORY_CHOICE = {
	SELF: 'SELF',
	OTHER: 'OTHER',
	// The Legal Representative already saved for this vehicle, when it's a
	// distinct person (not the investor and not a controlling party).
	EXISTING_REPRESENTATIVE: 'EXISTING_REPRESENTATIVE',
} as const;

/** The saved Legal Representative shown as a pre-selected signatory option. */
export interface SavedSignatory {
	firstName: string;
	lastName: string;
	email: string;
}

export interface PIVInfoForm {
	[JOIN_ALLOCATION_PIV_INFO_FORM.IS_PIV]: boolean;
	[JOIN_ALLOCATION_PIV_INFO_FORM.PIV_NAME]: string;
	[JOIN_ALLOCATION_PIV_INFO_FORM.PIV_REG_NUMBER]: string;
	[JOIN_ALLOCATION_PIV_INFO_FORM.PIV_COUNTRY]: string;
	[JOIN_ALLOCATION_PIV_INFO_FORM.PIV_CITY]: string;
	[JOIN_ALLOCATION_PIV_INFO_FORM.PIV_POST_CODE]: string;
	[JOIN_ALLOCATION_PIV_INFO_FORM.PIV_STREET]: string;
	[JOIN_ALLOCATION_PIV_INFO_FORM.PIV_HOUSE_NUMBER]: string;
	[JOIN_ALLOCATION_PIV_INFO_FORM.IS_REPRESENTATIVE_SAME_AS_INVESTOR]: boolean;
	[JOIN_ALLOCATION_PIV_INFO_FORM.REPRESENTATIVE_FIRST_NAME]: string;
	[JOIN_ALLOCATION_PIV_INFO_FORM.REPRESENTATIVE_LAST_NAME]: string;
	[JOIN_ALLOCATION_PIV_INFO_FORM.REPRESENTATIVE_EMAIL]: string;
	[JOIN_ALLOCATION_PIV_INFO_FORM.IS_CONTROLLING_PARTY_REQUIRED]: boolean;
	[JOIN_ALLOCATION_PIV_INFO_FORM.CONTROLLING_PARTIES]: ControllingPartyEntry[];
	[JOIN_ALLOCATION_PIV_INFO_FORM.SIGNATORY_CHOICE]: string;
	[JOIN_ALLOCATION_PIV_INFO_FORM.OTHER_SIGNATORY_FIRST_NAME]: string;
	[JOIN_ALLOCATION_PIV_INFO_FORM.OTHER_SIGNATORY_LAST_NAME]: string;
	[JOIN_ALLOCATION_PIV_INFO_FORM.OTHER_SIGNATORY_EMAIL]: string;
	[JOIN_ALLOCATION_PIV_INFO_FORM.SAVED_SIGNATORY]: SavedSignatory | null;
}
